/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { graphql } from 'gatsby';
import Hero from '../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/components/hero';
import {
  ContextualAnalyticsData,
  SCREEN,
  FireScreenAnalytics,
} from '@atlassian/analytics-bridge';
import {
  DisplayHeading,
  Status,
  StatusPanelProps,
} from '@af/design-system-docs-ui';
import CardLink from '../components/card-link';
import { GridLayout } from '@af/design-system-docs-ui';
import ThemedImg from '../components/themed-img';
import {
  UNSAFE_Stack as Stack,
  UNSAFE_Text as Text,
  UNSAFE_Box as Box,
  UNSAFE_Inline as Inline,
} from '@atlaskit/ds-explorations';
import {
  CATEGORY_NAMES,
  CATEGORY_SLUGS,
  getPageDescription,
  getPageTitle,
} from '../utils/constants';
import titleify from '../utils/titleify';
import MetaTags from '../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/components/meta-tags';

const pageTitle = getPageTitle(CATEGORY_NAMES.COMPONENTS);
const pageDescription = getPageDescription(CATEGORY_NAMES.COMPONENTS);

const contentContainerStyles = css({
  // eslint-disable-next-line
  padding: '32px 80px 80px',
});

type Component = {
  name: string;
  slug: string;
  title: string;
  description: string;
  status?: StatusPanelProps;
};

interface Data {
  components: {
    nodes: Component[];
  };
  heroLight: { publicURL: string };
  heroDark: { publicURL: string };
  thumbnailsLight: {
    nodes: { name: string; publicURL: string }[];
  };
  thumbnailsDark: {
    nodes: { name: string; publicURL: string }[];
  };
}

const ComponentsPage = ({ data }: { data: Data }) => (
  <ContextualAnalyticsData
    attributes={{
      name: 'componentsHome',
      entriesCount: data.components.nodes.length,
    }}
    sourceType={SCREEN}
    sourceName="index"
  >
    {/**
     * This page has its layout applied automatically,
     * so it must manually apply its meta tags.
     */}
    <MetaTags
      title={pageTitle}
      description={pageDescription}
      categorySlug={titleify(CATEGORY_SLUGS.COMPONENTS)}
    />
    <FireScreenAnalytics />
    <Hero
      css={{ gridArea: 'hero' }}
      headline={pageTitle}
      description={pageDescription}
      imageSrc={{
        light: data.heroLight.publicURL,
        dark: data.heroDark.publicURL,
      }}
      imageDimensions={{
        width: 768,
        height: 640,
      }}
    />
    <div css={contentContainerStyles}>
      <GridLayout columns={3}>
        {data.components.nodes.map(({ title, description, slug, status }) => {
          const imageSrcLight = data.thumbnailsLight.nodes.find(
            (thumb: { name: string }) => thumb.name === slug,
          )?.publicURL;
          const imageSrcDark = data.thumbnailsDark.nodes.find(
            (thumb: { name: string }) => thumb.name === slug,
          )?.publicURL;

          return (
            <CardLink key={slug} to={`/${CATEGORY_SLUGS.COMPONENTS}/${slug}`}>
              <Box padding="space.300" display="block">
                <Stack gap="space.200" alignItems="flexStart">
                  {!!imageSrcLight && !!imageSrcDark ? (
                    <ThemedImg
                      css={{
                        borderRadius: 4,
                        marginTop: 0,
                        width: '100%',
                        display: 'block',
                      }}
                      src={{
                        light: imageSrcLight,
                        dark: imageSrcDark,
                      }}
                      width="100%"
                      height="auto"
                      alt=""
                    />
                  ) : (
                    <Box
                      display="block"
                      width="100%"
                      UNSAFE_style={{ aspectRatio: '4.1/3' }}
                      backgroundColor="elevation.surface.sunken"
                    />
                  )}
                  <Inline
                    gap="space.0"
                    UNSAFE_style={{ width: '100%' }}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <DisplayHeading level="h700">{title}</DisplayHeading>
                    {status && <Status {...status} />}
                  </Inline>

                  <Text>
                    {description && description.length > 140
                      ? description.slice(0, 110) + ' …'
                      : description}
                  </Text>
                </Stack>
              </Box>
            </CardLink>
          );
        })}
      </GridLayout>
    </div>
  </ContextualAnalyticsData>
);

export const query = graphql`
  query {
    components: allWorkspaceInfo(
      sort: { fields: title, order: ASC }
      filter: { category: { eq: "Components" } }
    ) {
      nodes {
        description
        title
        slug
        status
      }
    }
    heroLight: file(relativePath: { eq: "light/components@2x.png" }) {
      publicURL
    }
    heroDark: file(relativePath: { eq: "dark/components@2x.png" }) {
      publicURL
    }
    thumbnailsLight: allFile(
      filter: { relativeDirectory: { eq: "light/component-thumbs" } }
    ) {
      nodes {
        name
        publicURL
      }
    }
    thumbnailsDark: allFile(
      filter: { relativeDirectory: { eq: "dark/component-thumbs" } }
    ) {
      nodes {
        name
        publicURL
      }
    }
  }
`;

export default ComponentsPage;
